import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Step,
  StepButton,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import MainContentBox from "src/Shared/Components/MainContentBox/MainContentBox";
import Title from "src/Shared/Components/Title/Title";
import { ToastService } from "src/Shared/Services";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";
import CompanyService from "../../Services/Company.service";
import { IRouteParams } from "../../Interfaces";
import { ClientStatus } from "../../Enums/ClientStatus.enum";
import { IOnboardingWizardState } from "../../Interfaces/IOnboardingWizardState";
import { useStore } from "src/Shared/Hooks";
import { stepValidationMessage } from "./OnboardingCapture.validation";
import OnboardingStepPanel from "./OnboardingStepPanel";
import OnboardingReviewStep from "./OnboardingReviewStep";
import { WriteThroughResult } from "./useWriteThrough";
import {
  isStepComplete,
  isStepReachable,
  ONBOARDING_STEP_LABELS,
  STEP_LOCK_REASON,
  STEP_REQUIREMENTS,
  stepFromParam,
  TOTAL_ONBOARDING_STEPS,
} from "./OnboardingWizard.utils";

export { ONBOARDING_STEP_LABELS } from "./OnboardingWizard.utils";

/**
 * Said when the step could not be left because its own save was refused.
 *
 * Deliberately not the reason itself: the writer has already raised the server's own message as a
 * toast ("That company code is already in use", "Company Name cannot be empty!"), and repeating it
 * here would leave the two to drift. This says the one thing the toast does not — that nothing
 * moved.
 */
const SAVE_FAILED_MESSAGE =
  "Your changes could not be saved, so this step is still open.";

/**
 * What a marked field can hand focus to, in preference order: a Select's own combobox ahead of the
 * aria-hidden native input MUI renders beside it, then plain inputs, textareas and the rich-text
 * surfaces step 6 uses.
 */
const FOCUSABLE_IN_FIELD =
  '[role="combobox"], input:not([type="hidden"]):not([aria-hidden="true"]), textarea, [contenteditable="true"]';

/**
 * Puts the caret in the first field a refused advance marked.
 *
 * Marking the fields is only half an answer on a step long enough to scroll — step 1 alone can
 * refuse on six of them, and the banner names the problem without saying where it is. MUI puts
 * .Mui-error on the label, the input wrapper and the helper text, so the field is the enclosing
 * form control; focus goes to whatever inside it can actually take it, and a field that has nothing
 * focusable is scrolled to instead (a checkbox group carries the class on its label, not a control).
 */
const focusFirstMarkedField = (container: HTMLElement | null) => {
  const marked = container?.querySelector<HTMLElement>(".Mui-error");
  if (!marked) return;

  const field = marked.closest<HTMLElement>(".MuiFormControl-root") ?? marked;
  const focusable = Array.from(field.querySelectorAll<HTMLElement>(FOCUSABLE_IN_FIELD)).find(
    (element) => !element.hasAttribute("disabled") && element.tabIndex >= 0,
  );

  (focusable ?? field).scrollIntoView({ behavior: "smooth", block: "center" });
  focusable?.focus({ preventScroll: true });
};

/**
 * The onboarding wizard shell (Feature #5586).
 *
 * It owns navigation, the freeze, and the read of the one shared completion engine — the same
 * engine the Prospects grid meter, the record checklist and the submit gate read, so the four can
 * never disagree (story 5588). Progress is never computed here.
 *
 * Steps 1, 3 and 4 are captured on the client record (one stored value per field, P.6); the
 * capture screens for steps 2, 5 and 6 arrive with features #5614 and #5587 and write into the
 * onboarding payload this shell already stores and reads.
 */
const OnboardingWizard: React.FC = () => {
  const { companyRowID } = useParams<Partial<IRouteParams>>();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    rootStore: {
      companyStore: { getProspectsCount, setOpenClientContext },
    },
  } = useStore();

  const [state, setState] = useState<IOnboardingWizardState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [stepError, setStepError] = useState<string>("");
  const [stateRevision, setStateRevision] = useState<number>(0);
  const [attempted, setAttempted] = useState<boolean>(false);
  const [refusals, setRefusals] = useState<number>(0);
  const stepPanelRef = useRef<HTMLDivElement | null>(null);
  const flushRef = useRef<(() => Promise<WriteThroughResult>) | null>(null);
  const registerFlush = useCallback((flush: () => Promise<WriteThroughResult>) => {
    flushRef.current = flush;
  }, []);

  /**
   * The open step's own verdict on itself, answered from its live fields with no round trip.
   *
   * Optional: a step that registers nothing keeps being judged the old way, on the server's reply
   * after the write. A step deregisters on unmount, so the next one can never inherit this.
   */
  const validateRef = useRef<(() => string | null) | null>(null);
  const registerValidate = useCallback((validate: (() => string | null) | null) => {
    validateRef.current = validate;
  }, []);

  /**
   * Commits the open step's pending write-through and says so.
   *
   * Every route out of a step goes through here, so the confirmation is raised once, where the save
   * actually happens. It is deliberately silent when there was nothing to write: most step changes
   * are someone reading through the wizard, and announcing a save then would claim something the
   * request log would not support. A failure already raises its own error toast inside the writer.
   */
  const flushWrites = async (): Promise<WriteThroughResult> =>
    flushRef.current ? flushRef.current() : "nothing";
  const announceSaved = (result: WriteThroughResult) => {
    if (result === "saved") ToastService.success("Changes saved");
  };
  const flushPending = async (): Promise<WriteThroughResult> => {
    const result = await flushWrites();
    announceSaved(result);
    return result;
  };

  /**
   * Runs after the render that marked the fields, so the classes it looks for are in the DOM.
   *
   * Keyed on a count of refusals rather than on `attempted`: that flag does not change when a
   * second Save & Continue is refused for the same reason, and that press should still take the
   * caret back to the problem.
   */
  useEffect(() => {
    if (!refusals) return;
    focusFirstMarkedField(stepPanelRef.current);
  }, [refusals]);

  const step = stepFromParam(searchParams.get("step"));

  /**
   * Reads the stored onboarding. `onLoaded` gets the state; `onFailed` runs whenever no state
   * arrives instead — the read failed, answered with an empty body, or there was no client to read —
   * so a caller waiting on one of the two is never left waiting.
   */
  const load = useCallback(
    (onLoaded?: (data: IOnboardingWizardState) => void, onFailed?: () => void) => {
      if (!companyRowID) {
        setLoading(false);
        if (onFailed) onFailed();
        return;
      }
      CompanyService.getOnboardingWizardState(companyRowID).subscribe({
        next: (data) => {
          setLoading(false);
          // An empty answer is treated as a failed read: the copy there is stays, and the caller
          // still hears back, so a step waiting on its entry reload opens rather than spinning.
          if (!data) {
            if (onFailed) onFailed();
            return;
          }
          setState(data);
          setStateRevision((revision) => revision + 1);
          // The app bar lights PROSPECTS or HOME from the open client. This route never loads the
          // client record, so it publishes the context itself from the state it already has —
          // otherwise the tab depended on whether the record had been visited first (bug 5688).
          setOpenClientContext({
            companyRowID: data.companyRowID || companyRowID,
            clientStatusID: data.clientStatusID,
          });
          if (onLoaded) onLoaded(data);
        },
        error: (error: AxiosError) => {
          setLoading(false);
          ToastService.axiosError(error);
          if (onFailed) onFailed();
        },
      });
    },
    [companyRowID],
  );

  useEffect(() => {
    setLoading(true);
    load();
  }, [companyRowID, load]);

  // A step number outside 1–7 falls back to step 1, and the step always sits in the URL so the
  // link can be shared and browser back works (story 5589).
  useEffect(() => {
    const raw = searchParams.get("step");
    if (`${step}` !== raw) setSearchParams({ step: `${step}` }, { replace: true });
  }, [step, searchParams, setSearchParams]);

  // Opening a step remembers how far the user has reached — navigation state only, never
  // completion (story 5588). A submitted onboarding is never written to (story 5592).
  useEffect(() => {
    if (!state || state.isSubmitted) return;
    if (step <= state.furthestStepReached) return;
    CompanyService.saveOnboardingDraft({ companyRowID, step }).subscribe({
      next: () => load(),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  }, [state, step, companyRowID, load]);

  /**
   * Re-reads the stored onboarding whenever a step is entered, and remounts the step panel when the
   * fresh copy lands.
   *
   * A step component seeds its own fields from this state once, on mount. Write-through saves go to
   * the server without touching this copy, so returning to a step could mount it against a payload
   * from before those saves — step 2 reset to TDDA with the checkboxes clear, step 6 showed four
   * unanswered questions while step 7 and the frozen record read No (bug 5708). Reloading on step
   * entry means a step always mounts against server truth.
   *
   * The revision is what forces the remount. It advances only when this state is deliberately
   * reloaded — on mount, on step entry, after an advance, or after a structural change such as
   * adding a site — never on a write-through, so typing is never interrupted.
   */
  const stepEntryLoaded = useRef(false);

  /**
   * The step whose entry reload has landed. Until it has, the step is not mounted: mounting it
   * against the copy from before would act on values the server no longer holds — step 3 fetched
   * the clinic list for the country step 1 had just changed away from, then again for the new one.
   * A reload that brings no state — failed, or answered empty — still opens the step, on the copy
   * there is, as a failed one always has.
   */
  const [enteredStep, setEnteredStep] = useState<number>(step);
  const currentStepRef = useRef<number>(step);
  currentStepRef.current = step;

  useEffect(() => {
    if (!stepEntryLoaded.current) {
      // The mount effect above already loaded this step.
      stepEntryLoaded.current = true;
      return;
    }
    const entering = step;
    // A slower reload for a step already left must not open that step's panel on this one.
    const landed = () => {
      if (currentStepRef.current === entering) setEnteredStep(entering);
    };
    load(landed, landed);
  }, [step, load]);

  /**
   * Leaving a step never validates (story 5589), but it must not lose anything either: any pending
   * write-through is flushed before the step changes, so the step-entry reload below reads a payload
   * that already includes the last keystroke (bugs 5684, 5708).
   */
  const goToStep = async (target: number) => {
    setStepError("");
    setAttempted(false);
    await flushPending();

    // Leaving step 1 — by the stepper, same as Save & Continue — is itself what confirms it: step 1
    // deliberately reads incomplete until the wizard has been left at least once (task 5838's
    // resolution, OnboardingProgressEvaluator's FurthestStepReached gate), so there is nothing to
    // validate here first. Add New Client (story 5574) already guarantees step 1's own data fields;
    // this is only ever a status transition, never blocked or delayed by the navigation below (story
    // 5589, task 5836).
    if (step === 1 && target !== 1 && state?.clientStatusID === ClientStatus.Prospect) {
      CompanyService.promoteToOnboarding(companyRowID).subscribe({
        next: () => {
          getProspectsCount();
          load();
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
    }

    setSearchParams({ step: `${target}` }, { replace: true });
  };

  /**
   * A step's own advance action validates that step and blocks with the reason (story 5589).
   * Leaving by the stepper, the record or the browser never validates — a half-finished step can
   * always be parked.
   *
   * Pending write-throughs are flushed first, so the check never judges the step against a
   * keystroke that has not reached the server yet.
   */
  const advance = async () => {
    setStepError("");
    setAttempted(false);

    // Nothing is written while the step is invalid. The flush below is a real save — it POSTs and
    // raises "Changes saved" — so running it before the gate meant a refused step still saved, and
    // confirmed it, on its way to being blocked: the field was marked required and the save plainly
    // worked, both at once (bug 5846).
    //
    // This is NOT the pre-check that caused bugs 5667, 5673 and 5713. That one judged this shell's
    // `state`, which only moves on an explicit reload and is therefore a keystroke behind, so the
    // first click after any edit always failed. This asks the step itself, which answers from the
    // form the user is typing into and can never be behind.
    const invalid = validateRef.current?.() ?? null;
    if (invalid) {
      setStepError(invalid);
      setAttempted(true);
      setRefusals((count) => count + 1);
      return;
    }

    // A refused write has to stop the advance here, because the reload below cannot see it. The
    // server still holds the values from before this step was edited, so validating against them
    // judges a version of the step the user is not looking at — and passes it, on the strength of
    // the very values the refused edit was replacing. That is how a marked, invalid Company Code
    // still advanced: the field was marked from the live form while the gate read a stale, valid
    // code off the server, and the typed one was discarded on the way past. Every server-side
    // refusal behaves this way, not just the code ones — a blank Company Name makes the whole
    // record patch fail, taking the code edit in it down too (bugs 5846, 6006).
    // Flushed without the confirmation: it is raised below only once the step is accepted. "Changes
    // saved" beside a refusal banner read as though the step had gone through (TC-6347-07).
    const flushed = await flushWrites();
    if (flushed === "failed") {
      setStepError(SAVE_FAILED_MESSAGE);
      setAttempted(true);
      setRefusals((count) => count + 1);
      return;
    }

    // Validated against freshly loaded state, never the copy this component is holding.
    //
    // There used to be a pre-check against that local copy which returned early on failure. The
    // flush above has just pushed the user's edits to the server, so the local copy is by definition
    // behind — and because the early return skipped the reload below, the state never caught up: the
    // first click after any edit always failed, every repeat failed identically, and only leaving the
    // step and coming back fixed it. That one early return produced "Please fix the highlighted
    // fields" on a complete form, "Select how the client is invoiced." with an arrangement chosen,
    // "Answer all 4 site questions (4 left)" with four answers, and every other message contradicted
    // by the visible state (bugs 5667, 5673, 5713).
    load((fresh) => {
      // Step 1 is judged on its own field rules here, not on stepsComplete[0]: that flag now also
      // requires the wizard to have been left once already (task 5838's resolution), so on the very
      // first Save & Continue it reads incomplete even when every field is genuinely valid — using it
      // to gate this button would block a correct step 1 forever. stepValidationMessage(fresh, 1)
      // re-checks the same five fields the server does, minus that visited condition, so a step 1
      // with a bad company code (or any other real error) still blocks exactly as steps 2–7 do; only
      // a step 1 that is actually correct is allowed through before it has ever been left (bug 6006 —
      // task 5838's fix had removed this check for step 1 entirely instead of just excluding the
      // visited condition, so nothing on step 1 ever blocked, invalid data included).
      const blocked =
        step === 1 ? Boolean(stepValidationMessage(fresh, 1)) : !isStepComplete(fresh, step);
      if (blocked) {
        setStepError(stepValidationMessage(fresh, step) || STEP_REQUIREMENTS[step - 1]);
        // The banner names the problem; the fields themselves now show which ones (bug 5668), and
        // the effect above puts the caret in the first of them.
        setAttempted(true);
        setRefusals((count) => count + 1);
        return;
      }
      announceSaved(flushed);
      // First successful advance past step 1 moves a Prospect to Onboarding (Feature 1 transition,
      // story 5598); any other status is untouched and a repeat is a safe no-op.
      if (step === 1 && fresh.clientStatusID === ClientStatus.Prospect) {
        CompanyService.promoteToOnboarding(companyRowID).subscribe({
          next: () => {
            getProspectsCount();
            load();
          },
          error: (error: AxiosError) => ToastService.axiosError(error),
        });
      }
      goToStep(Math.min(step + 1, TOTAL_ONBOARDING_STEPS));
    });
  };

  if (loading) {
    return (
      <MainContentBox>
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      </MainContentBox>
    );
  }

  if (!state) {
    return (
      <MainContentBox>
        <Alert severity="error">This client's onboarding could not be loaded.</Alert>
      </MainContentBox>
    );
  }

  /**
   * An archived client's onboarding does not open at all.
   *
   * This route lives outside the client workspace, so the sidebar lock that closes an archived
   * client's other sections never reaches it: `/company/{guid}/onboarding?step=1` was still a live,
   * writable wizard for a client that had been deleted. Every step writes through as it is typed, so
   * there is no read-only version of this screen worth showing — it is refused instead, and the record
   * itself stays readable at its own URL (CR.11).
   */
  if (state.isArchived) {
    return (
      <MainContentBox>
        <Alert severity="warning">
          <AlertTitle>This client has been archived</AlertTitle>
          Its onboarding is closed and cannot be edited. The archived record is still
          readable from the client's own page.{" "}
          <Link to={`/company/${companyRowID}/overview`}>Open the client record</Link>.
        </Alert>
      </MainContentBox>
    );
  }

  const progressValue = (state.completedSteps / state.totalSteps) * 100;

  return (
    <MainContentBox>
      <Title
        backButtonEnabled
        title={`Client Onboarding${state.companyName ? ` — ${state.companyName}` : ""}`}
      />

      {/* Story 5592: a submitted onboarding says so, with its date, time, submitter and a link to
          the SOP output — and offers no route to reopen it. */}
      {state.isSubmitted && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              component={Link}
              to={`/company/${companyRowID}/onboarding/sop`}
            >
              View SOP Output
            </Button>
          }
        >
          <AlertTitle>Submitted and read-only</AlertTitle>
          Submitted{" "}
          {state.submittedOn
            ? DateTimeUtils.dateToLocalTimeZone(state.submittedOn)
            : ""}
          {state.submittedBy ? ` by ${state.submittedBy}` : ""}. This onboarding is
          kept as the frozen record of the agreed configuration; the client record
          itself stays editable through the normal CRM management process.
        </Alert>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2">
          {state.completedSteps} of {state.totalSteps} steps complete
        </Typography>
        {state.isNational && <Chip size="small" label="National" />}
        {!state.isHealthTickEligible && (
          <Tooltip title="HealthTick is available to New Zealand clients only.">
            <Chip size="small" variant="outlined" label="TDDA only" />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ mt: 3, mb: 3 }}>
        <Stepper nonLinear activeStep={step - 1} alternativeLabel>
          {ONBOARDING_STEP_LABELS.map((label, index) => {
            const target = index + 1;
            const reachable = isStepReachable(target, state.maxReachableStep);
            return (
              <Step key={label} completed={Boolean(state.stepsComplete?.[index])}>
                <Tooltip title={reachable ? "" : STEP_LOCK_REASON}>
                  <span>
                    <StepButton
                      disabled={!reachable}
                      onClick={() => goToStep(target)}
                    >
                      {label}
                    </StepButton>
                  </span>
                </Tooltip>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      {stepError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setStepError("")}>
          {stepError}
        </Alert>
      )}

      {/* Scopes the marked-field search to the step's own fields, so it can never land on a
          .Mui-error belonging to something else on screen. */}
      <Box ref={stepPanelRef}>
        {enteredStep !== step ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : step === TOTAL_ONBOARDING_STEPS ? (
          <OnboardingReviewStep
            state={state}
            companyRowID={companyRowID}
            onGoToStep={goToStep}
            onChanged={load}
          />
        ) : (
          <OnboardingStepPanel
            key={`step-${step}-rev-${stateRevision}`}
            state={state}
            step={step}
            showFieldErrors={attempted}
            companyRowID={companyRowID}
            onChanged={load}
            registerFlush={registerFlush}
            registerValidate={registerValidate}
          />
        )}
      </Box>

      {step !== TOTAL_ONBOARDING_STEPS && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button disabled={step === 1} onClick={() => goToStep(step - 1)}>
            Back
          </Button>
          {/* Labelled Save & Continue to match the paper process, but the payload was already
              written through as it was typed — this button only validates and navigates. */}
          {/* A submitted onboarding is a point-in-time record: this button stayed enabled on every
              frozen step even though nothing it does is permitted (bug 5709). The stepper still
              moves between steps, so a submitted record remains fully readable. */}
          <Button variant="contained" onClick={advance} disabled={state.isSubmitted}>
            Save &amp; Continue
          </Button>
        </Box>
      )}
    </MainContentBox>
  );
};

export default observer(OnboardingWizard);
